import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppInsightsService } from '../app-insights.service';
import { AppService } from '../app.service';
import {
  ICallCategory, ICallTag, ICommDashboard, ICommDashboardFilter, ICommDashboardDrillThroughFilter,
  ICommCallsRequiringReviewItem, ICommUserPageLayout, ICommThreshold,
  ICommConfigurationSave, ICommAiScoreByFacility, ICommAiScoreByEmployee, ICommRecentCallsFilter,
  ICommCallLogFilter, ICommCallLogPagedResult, ICommCallLogRow, ICommCallSummary, ICommCallTranscript, IProspectCustomerCallLogFilter, IProspectCustomerCallLogResult,
  ICommSharedFilterState, ICommUserSummary, ICommUserSummaryFilter, ICustomerCallLogRowSave, ICustomerCallLogRowSaveResult
} from 'src/app/Shared/models/CommunicationCenter';
import { ISaveDeleteResponse, ISaveModel } from 'src/app/Shared/interface';
import { CommonMethodService } from 'src/app/Shared/common-method.service';

@Injectable({
  providedIn: 'root'
})
export class CommunicationCenterService {
  private baseURL = this.appService.config.apiBaseUrl;

  // Filter persistence shared across Communication Center pages (Dashboard <-> Call Log, plus surviving a
  // trip through Configuration and back) - held here since this service is a root singleton that outlives
  // any individual page component's lifecycle, unlike the page's own filter fields which reset on re-init.
  // Common to both pages: Date Range, Store/Store Group, Category, Direction, Sentiment. Call Log's own
  // CallTagId/Page/PageSize are page-specific and intentionally NOT part of this shared state.
  private sharedFilterState: ICommSharedFilterState | null = null;

  constructor(private http: HttpClient,
              private appinsights: AppInsightsService,
              private appService: AppService,
              private commonMethodService: CommonMethodService) { }

  // Copies (including FacilityIds and the mutable Date fields) so callers can never mutate the service's
  // source of truth by mutating the returned object in place - only setSharedFilterState is allowed to
  // change what's persisted.
  getSharedFilterState(): ICommSharedFilterState | null {
    return this.sharedFilterState
      ? {
        ...this.sharedFilterState,
        StartDate: new Date(this.sharedFilterState.StartDate),
        EndDate: new Date(this.sharedFilterState.EndDate),
        FacilityIds: [...this.sharedFilterState.FacilityIds]
      }
      : null;
  }

  setSharedFilterState(state: ICommSharedFilterState): void {
    this.sharedFilterState = {
      ...state,
      StartDate: new Date(state.StartDate),
      EndDate: new Date(state.EndDate),
      FacilityIds: [...state.FacilityIds]
    };
  }

  clearSharedFilterState(): void {
    this.sharedFilterState = null;
  }

  // One-time (consume-once) handoff from the dashboard's drill-through popup to the Call Log page: which
  // Call Tag to preselect.
  private pendingCallLogTagId: number | null = null;

  setPendingCallLogTagId(tagId: number | null): void {
    this.pendingCallLogTagId = tagId;
  }

  consumePendingCallLogTagId(): number | null {
    const tagId = this.pendingCallLogTagId;
    this.pendingCallLogTagId = null;
    return tagId;
  }

  // One-time hand-off from the dashboard's "Calls requiring Review" widget "View all" link to the Call Log
  // page: pre-filter to the widget's own predicate (AI Score below threshold AND not yet human-reviewed).
  private pendingCallLogRequiringReviewOnly = false;

  setPendingCallLogRequiringReviewOnly(requiringReviewOnly: boolean): void {
    this.pendingCallLogRequiringReviewOnly = requiringReviewOnly;
  }

  consumePendingCallLogRequiringReviewOnly(): boolean {
    const requiringReviewOnly = this.pendingCallLogRequiringReviewOnly;
    this.pendingCallLogRequiringReviewOnly = false;
    return requiringReviewOnly;
  }

  // Sidebar nav badge (image-134) - the Call Log page pushes its last loaded TotalCount here whenever it
  // loads, so the badge can show it without the layout (which wraps every Communication Center page,
  // including ones with no call data of their own) needing to fetch it independently.
  private totalCallCountSubject = new BehaviorSubject<number>(0);
  totalCallCount$ = this.totalCallCountSubject.asObservable();

  setTotalCallCount(count: number): void {
    this.totalCallCountSubject.next(count);
  }

  private options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  formatCallDateTime(value: string, format?: 'short' | 'MMM d, h:mm a' | 'M/d/yy, h:mm a' | 'EEE, MMM d, y' | 'h:mm a'): string {
    const wallTime = this.commonMethodService.formatDate2(value?.slice(0, 19));
    if (!wallTime || !format) return wallTime;
    const timestamp = Date.parse(value.slice(0, 19) + 'Z');
    const options: Record<string, Intl.DateTimeFormatOptions> = {
      'short': { year: '2-digit', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' },
      'M/d/yy, h:mm a': { year: '2-digit', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' },
      'MMM d, h:mm a': { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
      'EEE, MMM d, y': { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' },
      'h:mm a': { hour: 'numeric', minute: '2-digit' }
    };
    return new Intl.DateTimeFormat('en-US', { ...options[format], timeZone: 'UTC' }).format(timestamp);
  }

  private dateOnlyFilter(filter: ICommDashboardFilter) {
    return {
      ...filter,
      StartDate: this.commonMethodService.formatDate(filter.StartDate),
      EndDate: this.commonMethodService.formatDate(filter.EndDate)
    };
  }

  getDashboard(filter: ICommDashboardFilter): Observable<ICommDashboard> {
    return this.http.post<ICommDashboard>(`${this.baseURL}/CommunicationCenter/GetDashboard`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getDashboardDrillThrough(filter: ICommDashboardDrillThroughFilter): Observable<ICommCallsRequiringReviewItem[]> {
    return this.http.post<ICommCallsRequiringReviewItem[]>(`${this.baseURL}/CommunicationCenter/GetDashboardDrillThrough`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getStorePerformance(filter: ICommDashboardFilter): Observable<ICommAiScoreByFacility[]> {
    return this.http.post<ICommAiScoreByFacility[]>(`${this.baseURL}/CommunicationCenter/GetStorePerformance`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getUserAgentScoring(filter: ICommDashboardFilter): Observable<ICommAiScoreByEmployee[]> {
    return this.http.post<ICommAiScoreByEmployee[]>(`${this.baseURL}/CommunicationCenter/GetUserAgentScoring`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getUserSummary(filter: ICommUserSummaryFilter): Observable<ICommUserSummary> {
    return this.http.post<ICommUserSummary>(`${this.baseURL}/CommunicationCenter/GetUserSummary`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  private recentCallsDateOnlyFilter(filter: ICommRecentCallsFilter) {
    const { StartDate, EndDate, ...remainingFilter } = filter;
    return {
      ...remainingFilter,
      ...(StartDate ? { StartDate: this.commonMethodService.formatDate(StartDate) } : {}),
      ...(EndDate ? { EndDate: this.commonMethodService.formatDate(EndDate) } : {})
    };
  }

  getRecentCalls(filter: ICommRecentCallsFilter): Observable<ICommCallsRequiringReviewItem[]> {
    return this.http.post<ICommCallsRequiringReviewItem[]>(`${this.baseURL}/CommunicationCenter/GetRecentCalls`, this.recentCallsDateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getCallCategories(includeInactive: boolean = false): Observable<ICallCategory[]> {
    return this.http.get<ICallCategory[]>(`${this.baseURL}/CommunicationCenter/GetCallCategories?includeInactive=${includeInactive}`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  saveCallCategory(model: ICallCategory): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SaveCallCategory`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  setCallCategoryInactive(id: number, inactive: boolean): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SetCallCategoryInactive`, { id, inactive }, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  deleteCallCategory(id: number): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/DeleteCallCategory`, { id }, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getCallTags(includeInactive: boolean = false): Observable<ICallTag[]> {
    return this.http.get<ICallTag[]>(`${this.baseURL}/CommunicationCenter/GetCallTags?includeInactive=${includeInactive}`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  saveCallTag(model: ICallTag): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SaveCallTag`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  setCallTagInactive(id: number, inactive: boolean): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SetCallTagInactive`, { id, inactive }, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  deleteCallTag(id: number): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/DeleteCallTag`, { id }, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getThreshold(): Observable<number> {
    return this.http.get<number>(`${this.baseURL}/CommunicationCenter/GetThreshold`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  saveThreshold(model: ICommThreshold): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SaveThreshold`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  saveConfiguration(model: ICommConfigurationSave): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SaveConfiguration`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getLayout(pageKey: number): Observable<string> {
    return this.http.get(`${this.baseURL}/CommunicationCenter/GetLayout?pageKey=${pageKey}`, { responseType: 'text' })
      .pipe(map(res => res), catchError(this.handleError));
  }

  saveLayout(model: ICommUserPageLayout): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/SaveLayout`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  resetLayout(pageKey: number): Observable<ISaveDeleteResponse> {
    return this.http.post<ISaveDeleteResponse>(`${this.baseURL}/CommunicationCenter/ResetLayout`, { pageKey }, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getCallLog(filter: ICommCallLogFilter): Observable<ICommCallLogPagedResult> {
    return this.http.post<ICommCallLogPagedResult>(`${this.baseURL}/CommunicationCenter/GetCallLog`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  // Call Log Export: the server writes the CSV to Blob and returns its download link in Message.
  exportCallLog(filter: ICommCallLogFilter): Observable<ISaveModel> {
    return this.http.post<ISaveModel>(`${this.baseURL}/CommunicationCenter/ExportCallLog`, this.dateOnlyFilter(filter), this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  // Admin Call Log only - one call's row + Relink fields (Relink popup input, then the in-place row refresh after it).
  getCallLogRow(phoneId: number): Observable<ICommCallLogRow> {
    return this.http.get<ICommCallLogRow>(`${this.baseURL}/CommunicationCenter/GetCallLogRow?phoneId=${phoneId}`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getCallSummary(phoneId: number): Observable<ICommCallSummary> {
    return this.http.get<ICommCallSummary>(`${this.baseURL}/CommunicationCenter/GetCallSummary?phoneId=${phoneId}`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getCallTranscript(phoneId: number): Observable<ICommCallTranscript> {
    return this.http.get<ICommCallTranscript>(`${this.baseURL}/CommunicationCenter/GetCallTranscript?phoneId=${phoneId}`)
      .pipe(map(res => res), catchError(this.handleError));
  }

  getProspectCustomerCallLog(filter: IProspectCustomerCallLogFilter): Observable<IProspectCustomerCallLogResult> {
    return this.http.post<IProspectCustomerCallLogResult>(`${this.baseURL}/CommunicationCenter/GetProspectCustomerCallLog`, filter, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  // Customer/Prospect Call Log inline edit - one row's categories, tags and Human Score in a single save.
  saveProspectCustomerCallLogRow(model: ICustomerCallLogRowSave): Observable<ICustomerCallLogRowSaveResult> {
    return this.http.post<ICustomerCallLogRowSaveResult>(`${this.baseURL}/CommunicationCenter/SaveProspectCustomerCallLogRow`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  // Communication Center Call Summary single Save - the call's categories, tags and Human Score in one request.
  saveCallSummary(model: ICustomerCallLogRowSave): Observable<ICustomerCallLogRowSaveResult> {
    return this.http.post<ICustomerCallLogRowSaveResult>(`${this.baseURL}/CommunicationCenter/SaveCallSummary`, model, this.options)
      .pipe(map(res => res), catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    console.error('server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      this.appinsights.logEvent(errMessage);
      return throwError(errMessage);
    }
    return throwError(error || 'Node.js server error');
  }
}
